import { Box, Typography } from '@material-ui/core';
import { FormikProps } from 'formik';
import React from 'react';
import Loader from '../../../assets/icons/Loader';
import AlertBase from '../../../components/Alerts/AlertBase';
import FormikFormattedTextField from '../../../components/FormikInputWrappers/FormikFormattedTextField';
import FormikSelectInput from '../../../components/FormikInputWrappers/FormikSelectInput';
import RenderText from '../../../dictionaries/RenderText';
import {
  NegotiationOptionAlternative,
  NegotiationType,
  OutDebt,
  useGetOutDebtNegotiationOptionsLazyQuery,
} from '../../../generated/graphql';
import { useThrottle } from '../../../utils/useThrottle';
import { useNonPaymentReasonFormContext } from '../NonPaymentReasonFormContext';

interface PaymentCapacityFormProps {
  formik: FormikProps<any>;
  outDebt: OutDebt;
  alternative: NegotiationOptionAlternative;
}

type ValidationErrors = Record<string, string>;

const ECONOMIC_OPTIONS = [
  { label: 'Empleado', value: 'empleado' },
  { label: 'Independiente', value: 'independiente' },
  { label: 'Pensionado', value: 'pensionado' },
  { label: 'Desempleado', value: 'desempleado' },
];

const PaymentCapacityForm: React.FC<PaymentCapacityFormProps> = (
  { formik, outDebt, alternative },
) => {
  const [
    getOutDebtNegotiationOptions,
    { data, loading },
  ] = useGetOutDebtNegotiationOptionsLazyQuery();

  const nonPaymentReasonFormContext = useNonPaymentReasonFormContext();
  const { setErrors } = nonPaymentReasonFormContext || {};

  const { economic } = formik.values;
  const declaredIncome = parseInt(formik.values.declaredIncome, 10) || 0;
  const declaredPaymentCapacity = parseInt(formik.values.declaredPaymentCapacity, 10) || 0;

  const isUnemployed = economic === 'desempleado';
  const alternativeValue = alternative?.value || 0;

  // Ref for tracking last validated values
  const lastValidatedRef = React.useRef<{
    income: number;
    capacity: number;
  }>({ income: 0, capacity: 0 });

  // Ref to know if the query has been executed at least once
  const hasValidatedRef = React.useRef<boolean>(false);

  // Calculate available alternatives
  const calculatedAlternatives = React.useMemo(() => {
    return data?.outDebtNegotiationOptions.find(
      (option) => option.type === NegotiationType.Refinance,
    )?.alternatives;
  }, [ data ]);

  // Local (synchronous) validations
  const localErrors: ValidationErrors = React.useMemo(() => {
    const errs: ValidationErrors = {};

    if (isUnemployed) {
      errs.economic = 'paymentCapacityForm_economic-error';
    }

    // Only validate if values are entered
    if (declaredIncome > 0 && alternativeValue > 0) {
      if (alternativeValue > declaredIncome) {
        errs.declaredIncome = 'paymentCapacityForm_declaredIncome-error';
      }
    }

    if (declaredPaymentCapacity && alternativeValue > declaredPaymentCapacity) {
      errs.declaredPaymentCapacity = 'paymentCapacityForm_declaredPaymentCapacity-min-error';
    }

    if (declaredPaymentCapacity > declaredIncome) {
      errs.declaredPaymentCapacity = 'paymentCapacityForm_declaredPaymentCapacity-max-error';
    }

    return errs;
  }, [ isUnemployed, declaredIncome, declaredPaymentCapacity, alternativeValue ]);

  // Async alternative validation (only runs after the query)
  const alternativeError = React.useMemo(() => {
    // If no validation has been executed yet, do not show error
    if (!hasValidatedRef.current && !data) return null;

    // If the query did not return alternatives or is empty, it's an error
    if (!calculatedAlternatives || calculatedAlternatives.length === 0) {
      return 'paymentCapacityForm_alternative-no-options-error';
    }

    // If there are alternatives but the selected one is not present, it's an error
    const isValidAlternative = calculatedAlternatives.some(
      (calcAlt) => calcAlt.value === alternative.value
          && calcAlt.term === alternative.term,
    );

    if (!isValidAlternative) {
      return 'paymentCapacityForm_alternative-invalid-error';
    }

    return null;
  }, [ calculatedAlternatives, alternative, hasValidatedRef, data ]);

  // Combine all errors
  const allErrors: ValidationErrors = React.useMemo(() => {
    const combined = { ...localErrors };
    if (alternativeError) {
      combined.alternative = alternativeError;
    }
    return combined;
  }, [ localErrors, alternativeError ]);

  // Throttled validation function
  const validatePaymentCapacity = useThrottle(() => {
    getOutDebtNegotiationOptions({
      variables: {
        debtId: outDebt.id,
        declaredIncome,
        declaredPaymentCapacity,
      },
    });
  }, 1000);

  // Execute async validation on value change
  React.useEffect(() => {
    // Conditions to execute validation:
    // 1. There are no local errors
    // 2. Both fields have valid values
    // 3. The values have changed since the last validation
    const shouldValidate = Object.keys(localErrors).length === 0
      && declaredIncome > 0
      && declaredPaymentCapacity > 0
      && !isUnemployed
      && (lastValidatedRef.current.income !== declaredIncome
        || lastValidatedRef.current.capacity !== declaredPaymentCapacity);

    if (shouldValidate) {
      lastValidatedRef.current = {
        income: declaredIncome,
        capacity: declaredPaymentCapacity,
      };
      hasValidatedRef.current = true;
      validatePaymentCapacity();
    }
  }, [
    declaredIncome,
    declaredPaymentCapacity,
    isUnemployed,
    localErrors,
    validatePaymentCapacity,
  ]);

  React.useEffect(() => {
    if (
      declaredIncome > 0
      && declaredPaymentCapacity > 0
      && Object.keys(localErrors).length === 0
      && !isUnemployed
    ) {
      lastValidatedRef.current = {
        income: declaredIncome,
        capacity: declaredPaymentCapacity,
      };
      hasValidatedRef.current = true;
      getOutDebtNegotiationOptions({
        variables: {
          debtId: outDebt.id,
          declaredIncome,
          declaredPaymentCapacity,
        },
      });
    }
    // Run this effect only once on mount to validate payment capacity,
    // but only if the form already has stored values.
    // Subsequent changes are handled by the other useEffect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync errors with context
  React.useEffect(() => {
    setErrors?.(allErrors);
  }, [ allErrors, setErrors ]);

  const hasErrors = Object.keys(allErrors).length > 0;
  const hasAdjustableErrors = Object.keys(allErrors).some(
    (key) => key === 'declaredIncome' || key === 'declaredPaymentCapacity' || key === 'alternative',
  );

  return (
    <>
      <Typography
        variant="body1"
        className="mb-2"
        style={{ fontWeight: 600 }}
        data-test="cy-summaryFormGeneralTitle-text"
      >
        <RenderText
          isMarkdown
          textKey="summaryView_generalform_Title"
        />
      </Typography>

      <FormikSelectInput
        formik={formik}
        valueId="economic"
        label="Actividad Económica"
        options={ECONOMIC_OPTIONS}
        fullWidth
        className="mb-2"
        displayEmpty
        placeholder="Selecciona una opción"
        data-test="cy-economic-select"
      />

      <FormikFormattedTextField
        formik={formik}
        min={0}
        max={1000000000}
        valueId="declaredIncome"
        placeholder="Ingresa tu ingreso mensual"
        currency
        label="Ingresos mensuales"
        className="mb-2"
        data-test="cy-declaredIncome-input"
        fullWidth
      />

      <FormikFormattedTextField
        formik={formik}
        min={0}
        max={declaredIncome || 1000000000}
        valueId="declaredPaymentCapacity"
        placeholder="Ingresa tu capacidad de pago mensual"
        currency
        label="Confirma el valor que puedes destinar mensualmente para pagar esta cuota"
        className="mb-2"
        data-test="cy-declaredPaymentCapacity-input"
        fullWidth
      />

      {loading && (
      <Box display="flex" justifyContent="center" mt={2}>
        <Loader />
      </Box>
      )}

      {hasErrors && (
      <AlertBase
        type="restrictions"
        title="No podemos continuar con tu solicitud"
        dataTest="cy-paymentCapacityForm-errors-alert"
      >
        <ul>
          {Object.values(allErrors).map((err) => (
            <li key={err}>
              <RenderText textKey={err} />
            </li>
          ))}
        </ul>
        {hasAdjustableErrors && (
        <Typography variant="body2">
          <RenderText textKey="paymentCapacityForm_adjustInfo" />
        </Typography>
        )}
      </AlertBase>
      )}
    </>
  );
};

export default PaymentCapacityForm;
